import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Activity, Check, Copy, Search, Trash2, EyeOff, ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ProjectShell } from "@/components/projects/project-shell";
import { useProject } from "@/hooks/use-projects";
import {
  useProjectShares,
  useUpdateShare,
  useDeleteShare,
  type ShareRow,
} from "@/hooks/use-share";
import { cn } from "@/lib/utils";

type Props = { projectsBasePath: string };

function shareTitle(name: string | null, createdAt: string) {
  if (name && name.trim()) return name.trim();
  return new Date(createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function shareUrl(shortToken: string) {
  return `${window.location.origin}/s/${shortToken}`;
}

export function AllSharesPage({ projectsBasePath }: Props) {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const { data: shares, isLoading } = useProjectShares(projectId);

  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const projectPath = `${projectsBasePath}/${projectId}`;
  const sharesPath = `${projectPath}/shares`;

  const rows = useMemo(() => {
    const list = shares ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (s) =>
        shareTitle(s.name, s.createdAt).toLowerCase().includes(q) ||
        s.shortToken.toLowerCase().includes(q),
    );
  }, [shares, query]);

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = selected.size > 0 && !allChecked;

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  }
  function toggleOne(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function copyLink(s: ShareRow) {
    await navigator.clipboard.writeText(shareUrl(s.shortToken));
    setCopiedId(s.id);
    setTimeout(() => setCopiedId((c) => (c === s.id ? null : c)), 1500);
  }

  function openShare(s: ShareRow) {
    // Team preview — skips the guest identify form (Task 3).
    window.open(`${shareUrl(s.shortToken)}?preview=1`, "_blank", "noopener");
  }

  async function disableSelected() {
    const ids = [...selected];
    await Promise.all(
      ids.map((id) => updateShare.mutateAsync({ shareId: id, body: { visibilityEnabled: false } })),
    );
    setSelected(new Set());
  }
  async function deleteSelected() {
    const ids = [...selected];
    if (!confirm(`Delete ${ids.length} share link${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    await Promise.all(ids.map((id) => deleteShare.mutateAsync(id)));
    setSelected(new Set());
  }

  const selCount = selected.size;
  const soleSelected = selCount === 1 ? rows.find((r) => selected.has(r.id)) ?? null : null;

  return (
    <ProjectShell
      projectId={projectId}
      projectsBasePath={projectsBasePath}
      crumbs={[
        { label: project?.name ?? "…", to: projectPath },
        { label: "All Shares" },
      ]}
    >
      {/* Toolbar: search shares */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5 md:px-6">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Shares"
            className="h-9 pl-9"
          />
        </div>
      </div>

      {/* Scrolling table */}
      <div className="scrollbar-none min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-primary"
                />
              </th>
              <th className="px-2 py-3 font-medium">Title</th>
              <th className="px-2 py-3 font-medium">Link</th>
              <th className="px-2 py-3 font-medium">Visibility</th>
              <th className="px-2 py-3 font-medium">Last viewed</th>
              <th className="px-2 py-3 font-medium">Views</th>
              <th className="px-2 py-3 font-medium">Activity</th>
              <th className="px-2 py-3 font-medium">Created by</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-muted-foreground">
                {query ? "No shares match your search." : "No share links in this project yet."}
              </td></tr>
            )}
            {rows.map((s) => {
              const checked = selected.has(s.id);
              return (
                <tr
                  key={s.id}
                  className={cn(
                    "border-b border-border/70 transition-colors hover:bg-secondary/40",
                    checked && "bg-secondary/60",
                  )}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${shareTitle(s.name, s.createdAt)}`}
                      checked={checked}
                      onChange={() => toggleOne(s.id)}
                      className="h-4 w-4 accent-primary"
                    />
                  </td>
                  <td className="px-2 py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`${sharesPath}/${s.id}`)}
                      className="font-medium text-foreground hover:underline"
                    >
                      {shareTitle(s.name, s.createdAt)}
                    </button>
                    <div className="text-xs text-muted-foreground">{s.itemCount} item{s.itemCount === 1 ? "" : "s"}</div>
                  </td>
                  <td className="px-2 py-3">
                    <button
                      type="button"
                      onClick={() => copyLink(s)}
                      className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                      title={shareUrl(s.shortToken)}
                    >
                      {copiedId === s.id ? <Check className="h-3 w-3 shrink-0 text-green-500" /> : <Copy className="h-3 w-3 shrink-0" />}
                      <span className="truncate">/s/{s.shortToken}</span>
                    </button>
                  </td>
                  <td className="px-2 py-3">
                    <Switch
                      checked={s.visibilityEnabled}
                      onCheckedChange={(v) =>
                        updateShare.mutate({ shareId: s.id, body: { visibilityEnabled: v } })
                      }
                    />
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">
                    {s.lastViewedAt
                      ? new Date(s.lastViewedAt).toLocaleDateString()
                      : "Never viewed"}
                  </td>
                  <td className="px-2 py-3 tabular-nums text-muted-foreground">{s.viewCount}</td>
                  <td className="px-2 py-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`${sharesPath}/${s.id}?tab=activity`)}
                    >
                      <Activity className="h-3.5 w-3.5" />
                      View Activity
                    </Button>
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">{s.createdByName ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer selection bar */}
      {selCount > 0 && (
        <div className="flex shrink-0 items-center gap-3 border-t border-border bg-background px-4 py-2.5 md:px-6">
          <span className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{selCount} selected</span>
            {" · "}{shares?.length ?? 0} Shares
          </span>
          <div className="flex-1" />
          {soleSelected && (
            <>
              <Button variant="outline" size="sm" onClick={() => openShare(soleSelected)}>
                <ExternalLink className="h-4 w-4" /> Open Share
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`${sharesPath}/${soleSelected.id}`)}>
                <Pencil className="h-4 w-4" /> Edit Share
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={disableSelected} disabled={updateShare.isPending}>
            <EyeOff className="h-4 w-4" /> Disable
          </Button>
          <Button variant="destructive" size="sm" onClick={deleteSelected} disabled={deleteShare.isPending}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      )}
    </ProjectShell>
  );
}
